<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="modal-overlay" @click.self="close">
      <div class="custom-modal">
        <div class="modal-header">
          <h3>选择获取文件</h3>
          <button class="close-btn" @click="close">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="loading-state">
            <i class="fa-solid fa-spinner fa-spin"></i>
            正在获取文件列表...
          </div>
          <div v-else-if="!files || files.length === 0" class="empty-state">
            暂无可获取的文件
          </div>
          <div v-else>
            <!-- 分类文件夹筛选条（当存在多个分类时展示） -->
            <div v-if="availableFolders.length > 1" class="folder-filter-bar" @wheel="handleFilterWheel">
              <button
                class="filter-tab-btn"
                :class="{ active: activeFolder === 'all' }"
                @click="activeFolder = 'all'"
              >
                全部 ({{ files.length }})
              </button>
              <button
                v-for="folder in availableFolders"
                :key="folder"
                class="filter-tab-btn"
                :class="{ active: activeFolder === folder }"
                @click="activeFolder = folder"
              >
                <i class="fa-solid fa-folder-open"></i>
                {{ folder }} ({{ getFolderCount(folder) }})
              </button>
            </div>

            <div class="file-list">
              <div class="file-list-label">
                {{ activeFolder === 'all' ? `全部文件 (${filteredFiles.length})` : `分类：${activeFolder} (${filteredFiles.length})` }}
              </div>
              <div
                v-for="f in filteredFiles"
                :key="f.filePath || f.name"
                class="file-row"
                @click="handleDownload(f)"
                :title="'下载 ' + f.name"
              >
                <div class="file-info">
                  <i class="fa-solid fa-file-lines"></i>
                  <div class="file-name-block">
                    <span class="file-name">{{ f.name }}</span>
                    <span v-if="f.folder" class="folder-sub-label">
                      <i class="fa-solid fa-folder-closed"></i> {{ f.folder }}
                    </span>
                  </div>
                  <span class="file-size">{{ formatSize(f.size) }}</span>
                </div>
                <div class="file-actions">
                  <span class="arch-tag" :class="getArchTagClass(f.name)">{{ getArchTagLabel(f.name) }}</span>
                  <i class="fa-solid fa-cloud-arrow-down download-icon"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="close-footer-btn" @click="close">关闭</button>
        </div>
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<script>
import axios from 'axios'
import BaseServerUrl from '@/assets/BaseServerUrl'

const ARCH_RULES = [
  { pattern: /\.deb$/i, os: 'debian', label: 'Debian', tagClass: 'tag-linux' },
  { pattern: /\.rpm$/i, os: 'redhat', label: 'RPM', tagClass: 'tag-linux' },
  { pattern: /\.AppImage$/i, os: 'linux', label: 'Linux', tagClass: 'tag-linux' },
  { pattern: /\.flatpak$/i, os: 'linux', label: 'Flatpak', tagClass: 'tag-linux' },
  { pattern: /\.snap$/i, os: 'linux', label: 'Snap', tagClass: 'tag-linux' },
  { pattern: /\.apk$/i, os: 'android', label: 'Android', tagClass: 'tag-android' },
  { pattern: /\.dmg$/i, os: 'macos', label: 'macOS', tagClass: 'tag-macos' },
  { pattern: /\.pkg$/i, os: 'macos', label: 'macOS', tagClass: 'tag-macos' },
  { pattern: /\.exe$/i, os: 'windows', label: 'Windows', tagClass: 'tag-windows' },
  { pattern: /\.msi$/i, os: 'windows', label: 'Windows', tagClass: 'tag-windows' },
  { pattern: /\.zip$/i, os: 'unknown', label: 'ZIP', tagClass: 'tag-generic' },
  { pattern: /\.tar\.gz$/i, os: 'unknown', label: 'Tar.gz', tagClass: 'tag-generic' },
  { pattern: /\.tar\.xz$/i, os: 'unknown', label: 'Tar.xz', tagClass: 'tag-generic' },
  { pattern: /\.7z$/i, os: 'unknown', label: '7z', tagClass: 'tag-generic' },
]

const CPU_RULES = [
  { pattern: /amd64|x86_64|x64(?![a-z])/i, label: 'x64', arch: 'x64' },
  { pattern: /arm64|aarch64/i, label: 'ARM64', arch: 'arm64' },
  { pattern: /armv[67]|armhf/i, label: 'ARM', arch: 'arm' },
  { pattern: /i386|i686|x86(?!_64)/i, label: 'x86', arch: 'x86' },
  { pattern: /riscv64/i, label: 'RISC-V', arch: 'riscv64' },
  { pattern: /loongarch64/i, label: 'LoongArch', arch: 'loongarch64' },
]

export default {
  name: 'CloudDownloadModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    appId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['update:visible', 'download'],
  data() {
    return {
      files: [],
      activeFolder: 'all',
      loading: false,
      filesLoaded: false
    }
  },
  computed: {
    availableFolders() {
      const folders = new Set()
      this.files.forEach(f => {
        if (f.folder && f.folder.trim()) {
          folders.add(f.folder.trim())
        }
      })
      return Array.from(folders)
    },
    filteredFiles() {
      if (this.activeFolder === 'all') {
        return this.files
      }
      return this.files.filter(f => f.folder === this.activeFolder)
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.appId) {
        this.activeFolder = 'all'
        this.loadFiles()
      }
      if (!newVal) {
        this.files = []
        this.activeFolder = 'all'
        this.filesLoaded = false
      }
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false)
    },
    handleFilterWheel(e) {
      if (e.deltaY !== 0) {
        e.preventDefault()
        e.currentTarget.scrollLeft += e.deltaY
      }
    },
    getFolderCount(folder) {
      return this.files.filter(f => f.folder === folder).length
    },
    async loadFiles() {
      if (this.filesLoaded && this.files.length > 0) return
      this.loading = true
      this.files = []
      try {
        const response = await axios.get(`${BaseServerUrl}/api/software/${this.appId}/files`)
        if (response.data.success) {
          this.files = response.data.data || []
        }
      } catch (error) {
        console.error('获取文件列表失败:', error)
      } finally {
        this.loading = false
        this.filesLoaded = true
      }
    },
    handleDownload(file) {
      this.$emit('download', file)
    },
    formatSize(size) {
      if (!size) return 'Unknown'
      const mb = size / (1024 * 1024)
      return mb < 0.1 ? (size / 1024).toFixed(2) + ' KB' : mb.toFixed(2) + ' MB'
    },
    getArchTagLabel(fileName) {
      for (const rule of ARCH_RULES) {
        if (rule.pattern.test(fileName)) {
          let label = rule.label
          for (const cpu of CPU_RULES) {
            if (cpu.pattern.test(fileName)) {
              label += ' ' + cpu.label
              break
            }
          }
          return label
        }
      }
      return '其他'
    },
    getArchTagClass(fileName) {
      for (const rule of ARCH_RULES) {
        if (rule.pattern.test(fileName)) {
          return rule.tagClass
        }
      }
      return 'tag-generic'
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-dialog);
}

.custom-modal {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 28px;
  width: 90%;
  max-width: 550px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 85vh;
}

.modal-header {
  padding: 25px 30px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
}

.modal-header h3 {
  margin: 0;
  font-family: '猫啃网糖圆体';
  font-size: 1.25em;
  color: #1a1a1a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  transition: color 0.3s;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.loading-state, .empty-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-family: '爱点风雅黑';
}

.loading-state i {
  margin-right: 8px;
  color: #4CAF50;
}

.recommend-section {
  margin-bottom: 15px;
}

.recommend-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  color: #fff;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 10px;
  font-family: '爱点风雅黑';
}

.recommend-badge i {
  font-size: 0.9em;
}

.recommend-row {
  border-color: #4CAF50 !important;
  background: rgba(76, 175, 80, 0.06) !important;
  border-width: 2px !important;
}

.recommend-row:hover {
  background: rgba(76, 175, 80, 0.1) !important;
  border-color: #2E7D32 !important;
  box-shadow: 0 10px 25px rgba(76, 175, 80, 0.15);
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-list-label {
  font-size: 0.85em;
  color: #999;
  margin-bottom: 4px;
  font-family: '爱点风雅黑';
}

.file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;
}

.file-row:hover {
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.7);
  transform: translateX(8px) scale(1.02);
  box-shadow: 0 10px 25px rgba(76, 175, 80, 0.15);
}

.file-row.is-recommended {
  opacity: 0.7;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.file-info i {
  color: #4CAF50;
  font-size: 0.9em;
}

.file-row.recommend-row .file-info i {
  color: #4CAF50;
}

.folder-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;
  margin-bottom: 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(76, 175, 80, 0.35) transparent;
  -webkit-overflow-scrolling: touch;
}

.folder-filter-bar::-webkit-scrollbar {
  height: 4px;
}

.folder-filter-bar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.folder-filter-bar::-webkit-scrollbar-thumb {
  background: rgba(76, 175, 80, 0.3);
  border-radius: 4px;
  transition: background 0.25s ease;
}

.folder-filter-bar::-webkit-scrollbar-thumb:hover {
  background: rgba(76, 175, 80, 0.65);
}

.filter-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  font-size: 0.8em;
  font-family: '爱点风雅黑';
  color: #555;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s;
}

.filter-tab-btn:hover {
  background: rgba(255, 255, 255, 0.85);
  border-color: rgba(76, 175, 80, 0.4);
  color: #2e7d32;
}

.filter-tab-btn.active {
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25);
  font-weight: bold;
}

.file-name-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.folder-sub-label {
  font-size: 0.72em;
  color: #66bb6a;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: '爱点风雅黑';
}

.file-name {
  color: #333;
  font-size: 0.9em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: '爱点风雅黑';
  font-weight: bold;
}

.file-size {
  color: #999;
  font-size: 0.75em;
  margin-left: 4px;
  flex-shrink: 0;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}

.arch-tag {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: bold;
  white-space: nowrap;
  font-family: '爱点风雅黑';
  letter-spacing: 0.3px;
}

.tag-windows {
  background: rgba(76, 175, 80, 0.1);
  color: #2e7d32;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.tag-macos {
  background: rgba(64, 158, 255, 0.1);
  color: #1565c0;
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.tag-linux {
  background: rgba(255, 152, 0, 0.1);
  color: #e65100;
  border: 1px solid rgba(255, 152, 0, 0.2);
}

.tag-android {
  background: rgba(76, 175, 80, 0.1);
  color: #388e3c;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.tag-generic {
  background: rgba(144, 147, 153, 0.1);
  color: #666;
  border: 1px solid rgba(144, 147, 153, 0.15);
}

.download-icon {
  color: #4CAF50;
  font-size: 1.1em;
  opacity: 0.6;
  transition: all 0.3s;
}

.file-row:hover .download-icon {
  opacity: 1;
  transform: scale(1.1);
}

.modal-footer {
  padding: 20px 30px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: flex-end;
  background: rgba(255, 255, 255, 0.1);
}

.close-footer-btn {
  padding: 10px 25px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.5);
  color: #666;
  border-radius: 18px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  transition: all 0.3s;
}

.close-footer-btn:hover {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

/* Dark Theme */
.dark-theme .custom-modal {
  background: rgba(30, 30, 30, 0.7);
  color: #f5f5f7;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
}

.dark-theme .modal-header {
  border-color: rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.2);
}

.dark-theme .modal-header h3 {
  color: #f5f5f7;
}

.dark-theme .close-btn {
  color: #aaa;
}

.dark-theme .close-btn:hover {
  color: #f5f5f7;
}

.dark-theme .modal-footer {
  border-color: rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.2);
}

.dark-theme .file-row {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.05);
}

.dark-theme .file-row:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #4CAF50;
}

.dark-theme .recommend-row {
  background: rgba(76, 175, 80, 0.08) !important;
  border-color: #4CAF50 !important;
}

.dark-theme .recommend-row:hover {
  background: rgba(76, 175, 80, 0.15) !important;
}

.dark-theme .file-name {
  color: #f5f5f7;
}

.dark-theme .filter-tab-btn {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.1);
  color: #ccc;
}

.dark-theme .filter-tab-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #81c784;
}

.dark-theme .filter-tab-btn.active {
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  color: #fff;
  border-color: transparent;
}

.dark-theme .folder-sub-label {
  color: #81c784;
}

.dark-theme .folder-filter-bar {
  scrollbar-color: rgba(129, 199, 132, 0.4) transparent;
}

.dark-theme .folder-filter-bar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
}

.dark-theme .folder-filter-bar::-webkit-scrollbar-thumb {
  background: rgba(129, 199, 132, 0.35);
}

.dark-theme .folder-filter-bar::-webkit-scrollbar-thumb:hover {
  background: rgba(129, 199, 132, 0.7);
}

.dark-theme .file-info i {
  color: rgba(255, 255, 255, 0.5);
}

.dark-theme .file-row.recommend-row .file-info i {
  color: #4CAF50;
}

.dark-theme .file-size {
  color: rgba(255, 255, 255, 0.5);
}

.dark-theme .loading-state, .dark-theme .empty-state {
  color: rgba(255, 255, 255, 0.5);
}

.dark-theme .loading-state i {
  color: #4CAF50;
}

.dark-theme .recommend-badge {
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
}

.dark-theme .tag-windows {
  background: rgba(76, 175, 80, 0.15);
  color: #81c784;
  border-color: rgba(76, 175, 80, 0.3);
}

.dark-theme .tag-macos {
  background: rgba(64, 158, 255, 0.15);
  color: #79bbff;
  border-color: rgba(64, 158, 255, 0.3);
}

.dark-theme .tag-linux {
  background: rgba(255, 152, 0, 0.15);
  color: #eebe77;
  border-color: rgba(255, 152, 0, 0.3);
}

.dark-theme .tag-android {
  background: rgba(76, 175, 80, 0.15);
  color: #81c784;
  border-color: rgba(76, 175, 80, 0.3);
}

.dark-theme .tag-generic {
  background: rgba(144, 147, 153, 0.15);
  color: #b1b3b8;
  border-color: rgba(144, 147, 153, 0.3);
}

.dark-theme .close-footer-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #ccc;
}

.dark-theme .close-footer-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #4CAF50;
  color: #4CAF50;
}

.dark-theme .file-list-label {
  color: rgba(255, 255, 255, 0.5);
}

.dark-theme .download-icon {
  color: #4CAF50;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .custom-modal {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-leave-active .custom-modal {
  transition: transform 0.3s ease-in;
}

.modal-fade-enter-from .custom-modal {
  transform: scale(0.9) translateY(10px);
}

.modal-fade-leave-to .custom-modal {
  transform: scale(0.95) translateY(10px);
}

@media (max-width: 480px) {
  .custom-modal {
    width: 100%;
    border-radius: 20px 20px 0 0;
    position: absolute;
    bottom: 0;
    margin: 0;
    max-width: none;
    max-height: 90vh;
  }

  .modal-overlay {
    align-items: flex-end;
  }

  .modal-fade-enter-from .custom-modal,
  .modal-fade-leave-to .custom-modal {
    transform: translateY(100%);
  }
}

.distro-picker-modal .modal-body {
  padding: 25px;
}

.distro-hint {
  font-size: 0.9em;
  color: #666;
  margin: 0 0 20px 0;
  font-family: '爱点风雅黑';
  line-height: 1.6;
}

.distro-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.distro-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 12px;
  border: 2px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: rgba(255, 255, 255, 0.4);
  text-align: center;
}

.distro-card:hover {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.06);
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.12);
}

.distro-card-selected {
  border-color: #4CAF50 !important;
  background: rgba(76, 175, 80, 0.1) !important;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.2);
}

.distro-icon {
  font-size: 2em;
  color: #4CAF50;
  margin-bottom: 8px;
}

.distro-name {
  font-weight: bold;
  font-size: 0.9em;
  color: #333;
  font-family: '爱点风雅黑';
  margin-bottom: 4px;
}

.distro-desc {
  font-size: 0.75em;
  color: #999;
  font-family: '爱点风雅黑';
}

.distro-skip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: #999;
  font-size: 0.85em;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s;
  font-family: '爱点风雅黑';
}

.distro-skip:hover {
  color: #666;
  background: rgba(0, 0, 0, 0.03);
}

.distro-reset-btn {
  padding: 8px 18px;
  border: 1px solid rgba(255, 152, 0, 0.3);
  background: rgba(255, 152, 0, 0.08);
  color: #e65100;
  border-radius: 18px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  font-size: 0.85em;
  transition: all 0.3s;
  margin-right: auto;
}

.distro-reset-btn:hover {
  background: rgba(255, 152, 0, 0.15);
  border-color: rgba(255, 152, 0, 0.5);
  transform: translateY(-2px);
}

.dark-theme .distro-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}

.dark-theme .distro-card:hover {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.08);
}

.dark-theme .distro-card-selected {
  border-color: #4CAF50 !important;
  background: rgba(76, 175, 80, 0.12) !important;
}

.dark-theme .distro-icon {
  color: #81c784;
}

.dark-theme .distro-name {
  color: #f5f5f7;
}

.dark-theme .distro-desc {
  color: rgba(255, 255, 255, 0.4);
}

.dark-theme .distro-hint {
  color: rgba(255, 255, 255, 0.6);
}

.dark-theme .distro-skip {
  color: rgba(255, 255, 255, 0.4);
}

.dark-theme .distro-skip:hover {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
}

.dark-theme .distro-reset-btn {
  background: rgba(255, 152, 0, 0.1);
  border-color: rgba(255, 152, 0, 0.3);
  color: #eebe77;
}

.dark-theme .distro-reset-btn:hover {
  background: rgba(255, 152, 0, 0.2);
  border-color: rgba(255, 152, 0, 0.5);
}

@media (max-width: 480px) {
  .distro-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .distro-card {
    padding: 14px 8px;
  }

  .distro-icon {
    font-size: 1.5em;
  }
}

.system-incompatible-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 71, 87, 0.1);
  border: 1px solid rgba(255, 71, 87, 0.3);
  color: #ff4757;
  padding: 15px 20px;
  border-radius: 16px;
  margin-bottom: 20px;
  font-family: '爱点风雅黑';
  font-size: 0.9em;
  line-height: 1.4;
  box-shadow: 0 4px 15px rgba(255, 71, 87, 0.05);
}

.system-incompatible-warning i {
  font-size: 1.2em;
  flex-shrink: 0;
}

.dark-theme .system-incompatible-warning {
  background: rgba(255, 71, 87, 0.15);
  border-color: rgba(255, 71, 87, 0.4);
  color: #ff6b81;
}
</style>
